import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../auth/AuthContext";
import MyDatePicker from "../../components/MyDatePicker"; // relative path
import DriverDropdown from "../../components/DriverDropdown";
import VendorDropdown from "../../components/VendorDropdown";
import VehicleDropdown from "../../components/VehicleDropdown";
import CustomerDropdown from "../../components/CustomerDropdown";

import { createDocument } from "../../api/numberingApi";
import { calculateTotalDuration } from "../../utils/dateUtils";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";

import dayjs from "dayjs";

import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  useClientInfo,
  axiosInstance,
  SnackbarAlert,
  CategoryDropdown,
} from "../../utils/commonImports";

import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";

import ContactAutocompleteWithAddNew from "../../components/ContactAutocompleteWithAddNew";
import { useAppContext } from "../../context/AppContext";
import {
  normalizePeriodDate,
  validatePeriodDate,
} from "../../utils/periodValidation";
/* ------------------------------------------------------------------ */
/* Defaults                                                            */
/* ------------------------------------------------------------------ */
const getCurrentDatetimeLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const initialBooking = {
  client_id: null, // use null not ""
  customer_id: null,
  booking_no: "",
  accounting_type: "Confirmed",
  booking_date_time: getCurrentDatetimeLocal(),
  vehicle_id: null,
  vehicle_group_id: null,
  duty_type: "",
  booking_from: "",
  location_id: null,
  reference: "",
  reporting_date: "",
  release_date: "",
  reporting_time: "", // time fields as empty string OK, normalize later
  release_time: "",
  usage_days: 0,
  advance_amount: 0,
  paid_through: "",
  payment_date: "",
  cheque_no: "",
  payment_mode: "",
  booking_status: "Pending",
  representative_name: "",
  addon: false,
  customer_comments: "",
  remarks: "",
  is_vendor_booking: false,
  vendor_id: null,
  vendor_vehicle_number: "",
  order_by_id: null,
  order_by_title: "",
  order_by_name: "",
  order_by_email: "",
  order_by_mobile: "",
};

const initialPassenger = {
  client_id: null,
  used_by_id: null,
  used_by_title: "",
  used_by_name: "",
  used_by_mobile: "",
  used_by_email: "",
  used_by_department: "",
  report_location_id: null,
  report_address: "",
  drop_location_id: null,
  drop_address: "",
};

const initialItinerary = {
  client_id: null,
  from_date: "",
  to_date: "",
  days: 1,
  travel_description: "",
  hotel_name: "",
  hotel_address: "",
  hotel_phone: "",
};

const TITLE_OPTIONS = [
  { value: "", label: "Select" },
  "Mr.",
  "Ms.",
  "Mrs.",
  "Dr.",
];

const BOOKING_FROM_OPTIONS = [
  { value: "", label: "Select booking source" },
  "Web Tool",
  "Mail",
  "Phone",
  "Other Travel",
  "Online App",
  "Website",
];

const DUTY_TYPE_OPTIONS = [
  { value: "", label: "Select duty type" },
  { value: "Local", label: "Local" },
  { value: "Outstation", label: "Outstation" },
  { value: "EmployeeTransfer", label: "Employee Transfer" },
  { value: "AirportTransfer", label: "Airport Transfer" },
  { value: "Contract", label: "Monthly Contract" },
  { value: "Package", label: "Package" },
  { value: "OSDayRate", label: "OS Day Rate" },
  { value: "KmSlab", label: "Km Slab" },
];

const ACCOUNTING_TYPE_OPTIONS = [
  { value: "Confirmed", label: "Confirmed" },
  { value: "Estimation", label: "Estimation" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "", label: "Select payment mode" },
  "Postpaid",
  "COD",
];

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */
const FIELD_H = 40; // one height for every input, select, picker and dropdown

const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  primarySoft: "#eff6ff",
  page: "#f1f5f9",
  border: "#e2e8f0",
  borderStrong: "#cbd5e1",
  surface: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
};

/**
 * Shared control styling.
 * Applied to our own <Field> AND to the <Slot> wrapper that hosts external
 * dropdown / date-picker / autocomplete components, so every control ends up
 * the same height with the same border, font and label treatment.
 */
const controlSx = {
  minWidth: 0,
  width: "100%",

  "& .MuiFormControl-root, & .MuiAutocomplete-root": { width: "100%" },

  /* --- root box (MUI inputs, Autocomplete, and MUI X pickers) --- */
  "& .MuiInputBase-root:not(.MuiInputBase-multiline), & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root":
    {
      height: FIELD_H,
      boxSizing: "border-box",
      fontSize: 16,
      backgroundColor: "#fff",
      borderRadius: "8px",
      paddingTop: 0,
      paddingBottom: 0,
    },

  /* --- inner input / select text --- */
  "& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)": {
    height: "100%",
    boxSizing: "border-box",
    padding: "0 12px",
    fontSize: 16,
  },
  "& .MuiSelect-select.MuiSelect-select": {
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    padding: "0 32px 0 12px",
    minHeight: 0,
  },
  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    flexWrap: "nowrap",
  },
  "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
    height: "100%",
    fontSize: "16px !important",
    padding: "0 12px !important",
  },
  "& .MuiAutocomplete-endAdornment": { right: 6 },

  /* --- multiline (comments / remarks) keep natural height --- */
  "& .MuiInputBase-multiline": {
    fontSize: 16,
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "10px 12px",
  },

  /* --- labels --- */
  "& .MuiInputLabel-root": {
    fontSize: 14,
    color: C.muted,
    transform: "translate(13px, 11px) scale(1)",
  },
  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(13px, -8px) scale(0.85)",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },

  /* --- borders --- */
  "& .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: C.borderStrong,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: C.primary,
    borderWidth: 1,
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(29,78,216,0.12)",
  },

  /* --- tidy up leftovers from external components --- */
  "& .MuiFormHelperText-root": { fontSize: 11, mt: 0, ml: 0.5 },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const readOnlySx = {
  "& .MuiInputBase-root": { backgroundColor: `${C.surface} !important` },
  "& .MuiInputBase-input": { color: C.text },
};

const DATE_CONTAINER = { width: "100%", marginBottom: 0 };

const btnBase = {
  textTransform: "none",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: "8px",
  boxShadow: "none",
  minWidth: 0,
  px: 2.25,
  height: 36,
};

const primaryBtn = {
  ...btnBase,
  backgroundColor: C.primary,
  "&:hover": { backgroundColor: C.primaryHover, boxShadow: "none" },
};

const ghostBtn = {
  ...btnBase,
  borderColor: C.borderStrong,
  color: C.text,
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: C.surface, borderColor: C.muted },
};

const thSx = {
  fontSize: 15,
  fontWeight: 600,
  color: C.muted,
  whiteSpace: "nowrap",
  backgroundColor: C.surface,
  borderBottom: `1px solid ${C.border}`,
  borderTop: `1px solid ${C.border}`,
  px: 1.5,
  py: 1,
};

const tdSx = {
  fontSize: 15,
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: 0.5,
};

const wrapTd = {
  ...tdSx,
  whiteSpace: "normal",
  wordBreak: "break-word",
  maxWidth: 260,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "12px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 1.5,
        py: 0.5,
        minHeight: 40,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 4, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Column span helper for the responsive grid
const span = (n) => ({ gridColumn: { sm: "span 2", md: `span ${n}` } });

const SubLabel = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      mt: 1.25,
      mb: 0.75,
    }}
  >
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: C.text,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", backgroundColor: C.border }} />
  </Box>
);

// Wrapper for external dropdown / picker components
const Slot = ({ children, sx }) => (
  <Box sx={{ ...controlSx, ...sx }}>{children}</Box>
);

// Text input / select with consistent styling
const Field = ({ options, sx, InputProps, ...props }) => {
  const readOnly = !!InputProps?.readOnly;
  return (
    <TextField
      size="small"
      fullWidth
      select={!!options}
      InputProps={InputProps}
      sx={{ ...controlSx, ...(readOnly ? readOnlySx : {}), ...sx }}
      {...props}
    >
      {options &&
        options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          );
        })}
    </TextField>
  );
};

const IconBtn = ({ children, ...props }) => (
  <IconButton
    size="small"
    sx={{
      p: 0.75,
      borderRadius: "6px",
      "& svg": { fontSize: 17 },
      "&:hover": { backgroundColor: C.surface },
    }}
    {...props}
  >
    {children}
  </IconButton>
);

const EmptyRow = ({ cols, children }) => (
  <TableRow>
    <TableCell
      colSpan={cols}
      align="center"
      sx={{ ...tdSx, color: C.muted, py: 1, borderBottom: "none" }}
    >
      {children}
    </TableCell>
  </TableRow>
);

const BookingDateTimeField = ({ savedValue }) => {
  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    if (savedValue) return;
    const t = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(t);
  }, [savedValue]);

  const shown = savedValue ? dayjs(savedValue) : now;

  return (
    <Field
      label="Booking date & time"
      value={shown.format("DD/MM/YYYY HH:mm:ss")}
      InputProps={{ readOnly: true }}
    />
  );
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function BookingFormAdd({ editData, onCancel, onSuccess }) {
  const { clientId } = useClientInfo();
  const { token } = useAuth();
  const { fyid, selectedFinancialYear } = useAppContext();

  // Selected financial year (from the FY dropdown in the header).
  // Uses start_date / end_date; if the API names them differently, falls back
  // to the first key that looks like a start ("start"/"from") or end ("end"/"to").
  const fyYear = selectedFinancialYear || {};
  const pickFyDate = (pattern) => {
    const key = Object.keys(fyYear).find((k) => pattern.test(k));
    return key ? fyYear[key] : undefined;
  };
  const fyStartDate = fyYear.start_date ?? pickFyDate(/start|from/i);
  const fyEndDate = fyYear.end_date ?? pickFyDate(/end|to$/i);

  // Helps find the problem if the financial year dates cannot be read
  useEffect(() => {
    if (!normalizePeriodDate(fyStartDate) || !normalizePeriodDate(fyEndDate)) {
      console.warn(
        "[BookingFormAdd] Financial year dates not readable. selectedFinancialYear =",
        selectedFinancialYear,
      );
    }
  }, [selectedFinancialYear, fyStartDate, fyEndDate]);

  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState(initialBooking);
  const [passengers, setPassengers] = useState([]);
  const [passengerDraft, setPassengerDraft] = useState(initialPassenger);
  const [passengerEditIndex, setPassengerEditIndex] = useState(-1);

  const [itineraries, setItineraries] = useState([]);
  const [itineraryDraft, setItineraryDraft] = useState(initialItinerary);
  const [itineraryEditIndex, setItineraryEditIndex] = useState(-1);

  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);

  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);

  const [selectedVendor, setSelectedVendor] = useState({
    vendor_id: "",
    vendor_name: "",
    vendor_primary_contact_name: "",
    vendor_primary_contact_phone: "",
    vendor_primary_contact_email: "",
    vendor_service_city: "",
  });

  const [orderByContacts, setOrderByContacts] = useState([]);
  const [usedByContacts, setUsedByContacts] = useState([]);

  // Current booking date-time state for display
  const [currentDateTime, setCurrentDateTime] = useState(dayjs());
  const [selectedCustomerId, setSelectedCustomerId] = React.useState("");

  // Remembers which booking has already been loaded into the form, so the
  // edit-mode populate effect never overwrites what the user is typing.
  const loadedEditKeyRef = useRef(null);
  const bookingNumberRef = useRef(null);

  /* ------------------- Reporting date (financial year) ------------- */
  // Same rule as the VB.NET GetDateValidation:
  // allowed range = FY start - 31 days  to  FY end + 30 days
  // const GRACE_DAYS_BEFORE = 31;
  // const GRACE_DAYS_AFTER = 30;

  const reportingDateWrapRef = useRef(null);

  const getBookingDateOnly = () =>
    editData?.bookingid
      ? dayjs(form.booking_date_time).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

  const checkReportingDate = (value) => {
    if (!value) {
      return {
        valid: false,
        message: "Reporting Date is required.",
      };
    }

    const bookingDate = getBookingDateOnly();
    const reportingDate = String(value).slice(0, 10);

    if (!bookingDate) {
      return {
        valid: false,
        message: "Booking Date is required.",
      };
    }

    if (reportingDate < bookingDate) {
      return {
        valid: false,
        message: "Reporting Date cannot be earlier than Booking Date.",
      };
    }

    return {
      valid: true,
      message: "",
    };
  };

  // Red text under the field, shown immediately while the date is wrong
  const reportingDateError = form.reporting_date
    ? checkReportingDate(form.reporting_date).message
    : "";

  // Put the cursor back on the Reporting date input
  const focusReportingDate = () => {
    setTimeout(() => {
      reportingDateWrapRef.current?.querySelector("input")?.focus();
    }, 0);
  };

  // Runs when the user leaves the Reporting date field (like VB Validating):
  // wrong date -> message + cursor goes back to the field
  const handleReportingDateBlur = (e) => {
    // still inside the field (moving between day / month / year parts)
    if (e.currentTarget.contains(e.relatedTarget)) return;

    // opening the calendar popup should not trigger validation
    if (
      e.relatedTarget?.closest?.(
        '[role="dialog"], [role="presentation"], .MuiPopper-root',
      )
    ) {
      return;
    }

    const check = checkReportingDate(form.reporting_date);
    if (!check.valid) {
      SnackbarAlert(check.message, "error");
      focusReportingDate();
    }
  };

  // ---- VB-style "cannot go further" guard (nothing is disabled or greyed) ----
  const reportingDateInvalid = !checkReportingDate(form.reporting_date).valid;

  // true when the clicked / focused element is located AFTER the Reporting date field
  const isBeyondReportingDate = (target) => {
    const wrap = reportingDateWrapRef.current;
    if (!wrap || !target || typeof target.closest !== "function") return false;
    if (wrap.contains(target)) return false;
    if (
      target.closest(
        '[data-date-guard-skip], [role="dialog"], [role="presentation"], [role="listbox"], .MuiPopper-root, .MuiSnackbar-root',
      )
    ) {
      return false;
    }
    return Boolean(
      wrap.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
  };

  // Mouse: clicking any field after Reporting date is refused until date is valid
  const guardMouseDown = (e) => {
    if (reportingDateInvalid && isBeyondReportingDate(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      SnackbarAlert(checkReportingDate(form.reporting_date).message, "error");
      focusReportingDate();
    }
  };

  // Keyboard (Tab): focus is sent straight back to Reporting date
  const guardFocus = (e) => {
    if (reportingDateInvalid && isBeyondReportingDate(e.target)) {
      focusReportingDate();
    }
  };

  /* ------------------------- Order-by helpers ---------------------- */
  const selectedOrderByContact =
    orderByContacts.find(
      (c) =>
        `${c.customercontactfirstname || ""} ${c.customercontactlastname || ""}`.trim() ===
        (form.order_by_name || "").trim(),
    ) || {};

  const orderByMobile =
    form.order_by_mobile || selectedOrderByContact.customercontactmobile || "";

  const orderByEmail =
    form.order_by_email || selectedOrderByContact.customercontactemail || "";

  const findContactByName = (name) =>
    orderByContacts.find(
      (c) =>
        `${c.customercontactfirstname || ""} ${c.customercontactlastname || ""}`.trim() ===
        (name || "").trim(),
    );

  function onOrderByNameChange(event, newValue) {
    // 1) Convert object -> string
    let fullName = "";

    if (typeof newValue === "string") {
      fullName = newValue;
    } else if (newValue && typeof newValue === "object") {
      fullName =
        `${newValue.customercontactfirstname || ""} ${newValue.customercontactlastname || ""}`.trim();
    }

    // 2) Save the selected name
    handleFormChange({
      target: { name: "order_by_name", value: fullName },
    });

    // 3) Find contact using fullName (fall back to the object itself,
    //    e.g. a contact that was just added and isn't in state yet)
    const contact =
      findContactByName(fullName) ||
      (newValue && typeof newValue === "object" ? newValue : null);

    if (contact) {
      handleFormChange({
        target: {
          name: "order_by_mobile",
          value: contact.customercontactmobile || "",
        },
      });
      handleFormChange({
        target: {
          name: "order_by_email",
          value: contact.customercontactemail || "",
        },
      });

      // IMPORTANT:
      // bookings.order_by_id REFERENCES customers.customerid, NOT contactid.
      // handleSave() overwrites this the same way — keeping it consistent here.
      handleFormChange({
        target: {
          name: "order_by_id",
          value: form.customer_id || selectedCustomerId || null,
        },
      });

      handleFormChange({
        target: {
          name: "order_by_title",
          value: contact.customercontactsalutation || "",
        },
      });
    } else {
      handleFormChange({ target: { name: "order_by_mobile", value: "" } });
      handleFormChange({ target: { name: "order_by_email", value: "" } });
      handleFormChange({ target: { name: "order_by_title", value: "" } });
      handleFormChange({
        target: {
          name: "order_by_id",
          value: form.customer_id || selectedCustomerId || null,
        },
      });
    }
  }

  function handleNewOrderByContactAdded(newContact) {
    // Add new contact to the contacts list shown in dropdown
    setOrderByContacts((prev) => [...prev, newContact]);

    const fullName = `${newContact.customercontactfirstname || ""} ${
      newContact.customercontactlastname || ""
    }`.trim();

    handleFormChange({ target: { name: "order_by_name", value: fullName } });

    handleFormChange({
      target: {
        name: "order_by_mobile",
        value: newContact.customercontactmobile || "",
      },
    });

    handleFormChange({
      target: {
        name: "order_by_email",
        value: newContact.customercontactemail || "",
      },
    });

    // IMPORTANT:
    // bookings.order_by_id references customers.customerid
    // Therefore use the selected Customer ID, NOT contactid.
    handleFormChange({
      target: {
        name: "order_by_id",
        value: form.customer_id || selectedCustomerId || null,
      },
    });

    handleFormChange({
      target: {
        name: "order_by_title",
        value:
          newContact.customercontactsalutation ||
          newContact.customercontacttitle ||
          "",
      },
    });
  }

  function handleFormChange(event) {
    let { name, value } = event.target;

    // If value is object (from Autocomplete)
    if (typeof value === "object" && value !== null) {
      if (value.categoryid) value = value.categoryid;
      else if (value.inputValue) value = value.inputValue;
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "customer_id") {
      setSelectedCustomerId(value);
    }
  }

  /* ---------------------------- Effects ---------------------------- */
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  const currentDateTimeFormatted = currentDateTime.format(
    "DD/MM/YYYY HH:mm:ss",
  );

  useEffect(() => {
    let cancelled = false;

    async function fetchCustomers() {
      try {
        const response = await axiosInstance.get(
          `/api/customers?client_id=${clientId}`,
        );
        if (!cancelled) setCustomers(response.data);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch customers", error);
          setCustomers([]);
        }
      }
    }

    fetchCustomers();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Fetch once — these don't depend on which customer is selected
  useEffect(() => {
    if (!clientId) return;

    async function fetchStaticDropdowns() {
      try {
        const [vehRes, vendorRes, locationRes] = await Promise.all([
          axiosInstance.get(`/api/vehicles?client_id=${clientId}`),
          axiosInstance.get(`/api/vendors?client_id=${clientId}`),
          axiosInstance.get("/api/components/category", {
            params: { type: "Location", client_id: clientId },
          }),
        ]);

        setVehicles(vehRes.data);
        setVendors(vendorRes.data);
        setLocations(locationRes.data || []);
      } catch (err) {
        console.error("Failed to fetch vehicles/vendors/locations", err);
      } finally {
        setVehiclesLoaded(true);
      }
    }

    fetchStaticDropdowns();
  }, [clientId]);

  // Re-fetch whenever the selected customer changes
  // Load Order By contacts
  useEffect(() => {
    let cancelled = false;

    async function fetchOrderByContacts() {
      const customerId = form.customer_id || selectedCustomerId;

      if (!clientId || !customerId) {
        setOrderByContacts([]);
        return;
      }

      try {
        const res = await axiosInstance.get(
          `/api/customercontacts?client_id=${clientId}&customerid=${customerId}&contact_type=Orderby`,
        );

        if (!cancelled) {
          setOrderByContacts(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error(
          "Failed to load Order By contacts:",
          error.response?.data || error,
        );

        if (!cancelled) setOrderByContacts([]);
      }
    }

    fetchOrderByContacts();

    return () => {
      cancelled = true;
    };
  }, [clientId, form.customer_id, selectedCustomerId]);

  // Load Passenger (Usedby) contacts
  useEffect(() => {
    let cancelled = false;

    async function fetchUsedByContacts() {
      const customerId = form.customer_id || selectedCustomerId;

      if (!clientId || !customerId) {
        setUsedByContacts([]);
        return;
      }

      try {
        const res = await axiosInstance.get(
          `/api/customercontacts?client_id=${clientId}&customerid=${customerId}&contact_type=Usedby`,
        );

        if (!cancelled) {
          setUsedByContacts(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error(
          "Failed to load Passenger contacts:",
          error.response?.data || error,
        );

        if (!cancelled) setUsedByContacts([]);
      }
    }

    fetchUsedByContacts();

    return () => {
      cancelled = true;
    };
  }, [clientId, form.customer_id, selectedCustomerId]);

  // Populate the form in edit mode (after vehicles are loaded).
  // Runs only ONCE per booking so it can never wipe out the user's edits.
  useEffect(() => {
    if (!editData || !vehiclesLoaded) return;

    const key = editData.bookingid ?? "edit";
    if (loadedEditKeyRef.current === key) return;
    loadedEditKeyRef.current = key;

    // convert any date string to safe "YYYY-MM-DD"
    const toDateOnly = (val) => {
      if (!val) return "";

      // if ISO like 2026-03-04T00:00:00.000Z
      if (typeof val === "string") return val.substring(0, 10);

      // fallback (if Date object)
      const yyyy = val.getFullYear();
      const mm = String(val.getMonth() + 1).padStart(2, "0");
      const dd = String(val.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    let updatedForm = {
      ...initialBooking,
      ...editData,

      reporting_date: toDateOnly(editData.reporting_date),
      release_date: toDateOnly(editData.release_date),

      addon: editData.addon === "Yes" || editData.addon === true,
    };

    const vehicleObj = vehicles.find(
      (v) => v.vehicle_id === editData.vehicle_id,
    );

    if (vehicleObj) {
      updatedForm.vehicle_number = vehicleObj.vehicle_number;
      updatedForm.vehicle_type_name = vehicleObj.vehicle_type_name;

      // Prefer what was saved on the booking; fall back to the vehicle's default driver.
      updatedForm.driver_id =
        editData.driver_id || vehicleObj.vehicle_driver_id || null;
      updatedForm.driver_name =
        editData.driver_name || vehicleObj.vehicle_driver_name || "";
      updatedForm.driver_mobile =
        editData.driver_mobile || vehicleObj.vehicle_driver_mobile_no || "";
    } else {
      updatedForm.vehicle_group_id = editData.vehicle_group_id || null;
    }

    setForm(updatedForm);
    setSelectedCustomerId(updatedForm.customer_id || "");
    setPassengers(editData.passengers || []);
    setItineraries(
      (editData.itineraries || []).map((it) => ({
        ...it,
        from_date: toDateOnly(it.from_date),
        to_date: toDateOnly(it.to_date),
      })),
    );
  }, [editData, vehicles, vehiclesLoaded]);

  /* ------------------- Vehicle / vendor handlers ------------------- */
  const handleVehicleGroupChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      vehicle_group_id: value !== "" ? Number(value) : null,
    }));
  };

  const handleVehicleChange = (vehicle) => {
    if (!vehicle) {
      setForm((prev) => ({
        ...prev,
        vehicle_id: null,
        vehicle_number: "",
        driver_id: "",
        driver_name: "",
        driver_mobile: "",
      }));
      return;
    }

    setForm((prev) => {
      const shouldUpdateDriver =
        !prev.driver_id || prev.driver_id === prev.vehicle_driver_id;

      return {
        ...prev,
        vehicle_id: vehicle.vehicle_id || null, // Store vehicle_id
        vehicle_number: vehicle.vehicle_number || "",
        vehicle_driver_id: vehicle.vehicle_driver_id || "",

        ...(shouldUpdateDriver
          ? {
              driver_id: vehicle.vehicle_driver_id || "",
              driver_name: vehicle.vehicle_driver_name || "",
              driver_mobile: vehicle.vehicle_driver_mobile_no || "",
            }
          : {}),
      };
    });
  };

  // Handle updates from the VendorDropdown component
  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setForm((prev) => ({ ...prev, vendor_id: vendor?.vendor_id || null }));
  };

  /* ----------------------- Passenger handlers ---------------------- */
  // Autofill inputs on passenger name select
  function onUsedByNameChange(event, newValue) {
    let fullName = "";

    if (typeof newValue === "string") {
      fullName = newValue;
    } else if (newValue && typeof newValue === "object") {
      fullName =
        `${newValue.customercontactfirstname || ""} ${newValue.customercontactlastname || ""}`.trim();
    }

    setPassengerDraft((prev) => ({ ...prev, used_by_name: fullName }));

    // Fall back to the object itself (e.g. a contact that was just added)
    const contact =
      usedByContacts.find(
        (c) =>
          `${c.customercontactfirstname || ""} ${c.customercontactlastname || ""}`.trim() ===
          (fullName || "").trim(),
      ) || (newValue && typeof newValue === "object" ? newValue : null);

    if (contact) {
      const reportAddress = [
        contact.address,
        contact.area_location,
        contact.city,
        contact.postal_code,
      ]
        .filter(Boolean)
        .join(", ");

      setPassengerDraft((prev) => ({
        ...prev,
        used_by_mobile: contact.customercontactmobile || "",
        used_by_email: contact.customercontactemail || "",
        used_by_title: contact.customercontactsalutation || "",
        used_by_department: contact.customercontactdepartment || "",
        used_by_id: contact.contactid || "",
        report_address: reportAddress,
        drop_address: prev.drop_address || "",
      }));
    } else {
      setPassengerDraft((prev) => ({
        ...prev,
        used_by_mobile: "",
        used_by_email: "",
        used_by_title: "",
        used_by_department: "",
        used_by_id: "",
        report_address: "",
        drop_address: "",
      }));
    }
  }

  // Controlled form input handler
  function handlePassengerDraftChange(event) {
    const { name, value } = event.target;
    setPassengerDraft((prev) => ({ ...prev, [name]: value }));
  }

  // Add or update passenger with validation
  function addOrUpdatePassenger() {
    if (
      !passengerDraft.used_by_name?.trim() ||
      !passengerDraft.report_address?.trim() ||
      !passengerDraft.drop_address?.trim()
    ) {
      SnackbarAlert("Please fill passenger name, report and drop locations.");
      return;
    }

    if (passengerEditIndex === -1) {
      setPassengers((ps) => [...ps, { ...passengerDraft }]);
    } else {
      setPassengers((ps) =>
        ps.map((p, i) =>
          i === passengerEditIndex ? { ...passengerDraft } : p,
        ),
      );
      setPassengerEditIndex(-1);
    }

    setPassengerDraft(initialPassenger);
  }

  function cancelPassengerEdit() {
    setPassengerEditIndex(-1);
    setPassengerDraft(initialPassenger);
  }

  // Fill passenger draft for editing
  function editPassenger(index) {
    setPassengerEditIndex(index);
    setPassengerDraft({ ...initialPassenger, ...passengers[index] });
  }

  // Delete passenger by index
  function deletePassenger(index) {
    setPassengers((ps) => ps.filter((_, i) => i !== index));
    if (index === passengerEditIndex) {
      setPassengerEditIndex(-1);
      setPassengerDraft(initialPassenger);
    }
  }

  function handleNewUsedByContactAdded(newContact) {
    setUsedByContacts((prev) => [...prev, newContact]);
    setPassengerDraft({
      ...initialPassenger,
      used_by_id: newContact.contactid || newContact.customercontactid || "",
      used_by_name: `${newContact.customercontactfirstname || ""} ${
        newContact.customercontactlastname || ""
      }`.trim(),
      used_by_mobile: newContact.customercontactmobile || "",
      used_by_email: newContact.customercontactemail || "",
      used_by_title:
        newContact.customercontactsalutation ||
        newContact.customercontacttitle ||
        "",
      used_by_department: newContact.customercontactdepartment || "",
      report_location_id: "",
      report_address: "",
      drop_location_id: "",
      drop_address: "",
    });
  }

  // Used by the passenger-name autocomplete.
  // Typing must stay free-form, so only the name changes here
  // (title / mobile / email / department are kept as they are).
  function handlePassengerFormChange(event) {
    const { name, value } = event.target;

    setPassengerDraft((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "used_by_name" ? { used_by_id: null } : {}),
    }));
  }

  /* ----------------------- Itinerary handlers ---------------------- */
  // Date pickers
  function handleItineraryDraftChange(field, date) {
    const value = date ? dayjs(date).format("YYYY-MM-DD") : "";
    setItineraryDraft((prev) => ({ ...prev, [field]: value }));
  }

  // Text / number inputs
  function handleItineraryFieldChange(event) {
    const { name, value } = event.target;
    setItineraryDraft((prev) => ({ ...prev, [name]: value }));
  }

  function addOrUpdateItinerary() {
    if (!itineraryDraft.from_date || !itineraryDraft.to_date) {
      SnackbarAlert("Please select from and to dates.");
      return;
    }
    if (itineraryEditIndex === -1) {
      setItineraries((its) => [...its, itineraryDraft]);
    } else {
      setItineraries((its) =>
        its.map((it, i) => (i === itineraryEditIndex ? itineraryDraft : it)),
      );
      setItineraryEditIndex(-1);
    }
    setItineraryDraft(initialItinerary);
  }

  function cancelItineraryEdit() {
    setItineraryEditIndex(-1);
    setItineraryDraft(initialItinerary);
  }

  function editItinerary(i) {
    setItineraryEditIndex(i);
    setItineraryDraft(itineraries[i]);
  }

  function deleteItinerary(i) {
    setItineraries((its) => its.filter((_, idx) => idx !== i));
  }

  /* ------------------------------ Save ----------------------------- */
  async function handleSave() {
    if (isSaving) return;

    if (!clientId) {
      SnackbarAlert("Client ID missing", "error");
      return;
    }

    const isEdit = Boolean(editData?.bookingid);
    const editId = editData?.bookingid;

    // New bookings are stamped when saved; edited bookings keep their saved value.
    const bookingDateTime = isEdit
      ? form.booking_date_time
      : getCurrentDatetimeLocal();

    if (!bookingDateTime || !dayjs(bookingDateTime).isValid()) {
      SnackbarAlert("Booking Date is required.", "error");
      return;
    }

    const bookingDate = dayjs(bookingDateTime).format("YYYY-MM-DD");

    // ==========================================================
    // DATE VALIDATION
    // Booking Date is NOT tied to the financial year.
    // The financial year is decided by Reporting Date (on the backend).
    // ==========================================================
    if (!form.reporting_date) {
      SnackbarAlert("Reporting Date is required.", "error");
      focusReportingDate();
      return;
    }

    const reportingDate = String(form.reporting_date).slice(0, 10);

    if (reportingDate < bookingDate) {
      SnackbarAlert(
        "Reporting Date cannot be earlier than Booking Date.",
        "error",
      );
      focusReportingDate();
      return;
    }

    if (form.release_date) {
      const releaseDate = String(form.release_date).slice(0, 10);
      if (releaseDate < reportingDate) {
        SnackbarAlert(
          "Release Date cannot be earlier than Reporting Date.",
          "error",
        );
        return;
      }
    }

    // ==========================================================
    // BASIC VALIDATION
    // ==========================================================
    if (!form.customer_id) {
      SnackbarAlert("Please select customer.", "error");
      return;
    }

    setIsSaving(true);

    try {
      // ==========================================================
      // ALLOWED BOOKING FIELDS
      // (booking_no and fyid are NOT sent; the backend generates them)
      // ==========================================================
      const allowedBookingFields = [
        "booking_date_time",
        "booking_from",
        "accounting_type",
        "location_id",
        "reference",
        "customer_id",

        // Order By
        "order_by_id",
        "order_by_title",
        "order_by_name",
        "order_by_email",
        "order_by_mobile",

        "duty_type",
        "vehicle_group_id",
        "vehicle_id",
        "driver_name",
        "driver_id",
        "driver_mobile",
        "is_vendor_booking",
        "vendor_id",
        "vendor_vehicle_number",
        "reporting_date",
        "release_date",
        "reporting_time",
        "release_time",
        "usage_days",
        "advance_amount",
        "paid_through",
        "payment_date",
        "cheque_no",
        "payment_mode",
        "booking_status",
        "representative_name",
        "addon",
        "customer_comments",
        "remarks",
      ];

      const filteredForm = {};
      for (const key of allowedBookingFields) {
        if (key in form) {
          filteredForm[key] = form[key];
        }
      }

      // ==========================================================
      // NORMALIZE DATES
      // ==========================================================
      // Edit: don't resend the saved timestamp (server keeps it, no timezone shift).
      if (isEdit) {
        delete filteredForm.booking_date_time;
      } else {
        filteredForm.booking_date_time = bookingDateTime;
      }
      filteredForm.reporting_date = form.reporting_date || "";
      filteredForm.release_date = form.release_date || "";

      // ==========================================================
      // ADDON
      // ==========================================================
      filteredForm.addon = form.addon ? "Yes" : "No";

      // ==========================================================
      // ORDER BY
      // ==========================================================
      const selectedOrderBy =
        orderByContacts.find(
          (c) =>
            `${c.customercontactfirstname || ""} ${
              c.customercontactlastname || ""
            }`.trim() === (form.order_by_name || "").trim(),
        ) || {};

      filteredForm.order_by_name = (form.order_by_name || "").trim();

      filteredForm.order_by_mobile = (
        form.order_by_mobile ||
        selectedOrderBy.customercontactmobile ||
        ""
      ).trim();

      filteredForm.order_by_email = (
        form.order_by_email ||
        selectedOrderBy.customercontactemail ||
        ""
      ).trim();

      // bookings.order_by_id REFERENCES customers.customerid
      filteredForm.order_by_id = form.customer_id || selectedCustomerId || null;

      filteredForm.order_by_title =
        form.order_by_title ||
        selectedOrderBy.customercontactsalutation ||
        selectedOrderBy.customercontacttitle ||
        "";

      if (!filteredForm.order_by_name || !filteredForm.order_by_mobile) {
        SnackbarAlert("Order By name/mobile required.", "error");
        return;
      }

      // ==========================================================
      // PASSENGERS
      // ==========================================================
      const fixedPassengers = (passengers || []).map((p) => ({
        ...p,
        client_id: clientId,

        used_by_id: p.used_by_id || null,
        used_by_title: p.used_by_title || "",
        used_by_name: p.used_by_name || "",
        used_by_mobile: p.used_by_mobile || "",
        used_by_email: p.used_by_email || "",
        used_by_department: p.used_by_department || "",

        report_location_id: p.report_location_id || null,
        report_address: p.report_address || "",

        drop_location_id: p.drop_location_id || null,
        drop_address: p.drop_address || "",
      }));

      // ==========================================================
      // ITINERARIES
      // ==========================================================
      const cleanItineraries = (itineraries || [])
        .map((it) => ({
          ...it,
          client_id: clientId,
          from_date: it.from_date ? it.from_date.slice(0, 10) : null,
          to_date: it.to_date ? it.to_date.slice(0, 10) : null,
          days: Number(it.days || 1),
          travel_description: it.travel_description || "",
          hotel_name: it.hotel_name || "",
          hotel_address: it.hotel_address || "",
          hotel_phone: it.hotel_phone || "",
        }))
        .filter((it) => it.from_date && it.to_date && it.days);

      // ==========================================================
      // FINAL PAYLOAD
      // Backend generates booking_no and derives fyid from reporting_date.
      // ==========================================================
      const payload = {
        ...filteredForm,
        client_id: clientId,
        passengers: fixedPassengers,
        itineraries: cleanItineraries,
      };

      // ==========================================================
      // SAVE REQUEST
      // ==========================================================
      const res =
        isEdit && editId
          ? await axiosInstance.put(`/api/bookings/${editId}`, payload)
          : await axiosInstance.post(`/api/bookings`, payload);

      const saved = res?.data || {};
      const label = saved.booking_no ? ` ${saved.booking_no}` : "";
      const fyNote = saved.fy_name ? ` (FY ${saved.fy_name})` : "";

      SnackbarAlert(
        isEdit
          ? `Booking${label} updated successfully${fyNote}`
          : `Booking${label} created successfully${fyNote}`,
        "success",
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Save booking failed:", error?.response?.data || error);

      SnackbarAlert(
        error?.response?.data?.error ||
          "Failed to save booking. Please try again.",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  }

  /* ------------------- Date / time validation ---------------------- */
  // Convert YYYY-MM-DD to local Date object (prevents timezone shift)
  const safeDateObject = (dateStr) => {
    if (!dateStr) return null;

    // If already Date object
    if (dateStr instanceof Date) return dateStr;

    // "YYYY-MM-DD"
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split("-").map(Number);
      return new Date(y, m - 1, d); // LOCAL SAFE
    }

    // ISO "YYYY-MM-DDTHH:mm:ss"
    if (typeof dateStr === "string" && dateStr.includes("T")) {
      const iso = dateStr.substring(0, 10);
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d); // LOCAL SAFE
    }

    return null;
  };

  const isReleaseDateTimeValid = (repDate, repTime, relDate, relTime) => {
    if (!repDate || !relDate) return true;

    const repDateObj = safeDateObject(repDate);
    const relDateObj = safeDateObject(relDate);

    if (!repDateObj || !relDateObj) return true;

    // Different dates — date comparison is enough
    if (repDateObj.getTime() !== relDateObj.getTime()) {
      return relDateObj.getTime() >= repDateObj.getTime();
    }

    // Same date — also compare times if both are provided
    if (repTime && relTime) {
      return relTime >= repTime; // "HH:mm" strings compare correctly as-is
    }

    // Same date, one or both times missing — can't fully validate, allow it
    return true;
  };

  const handleDateChange = (field, value) => {
    const parsed = value ? dayjs(value) : null;
    const normalizedValue =
      parsed && parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";

    setForm((prev) => {
      const newForm = { ...prev, [field]: normalizedValue };

      if (
        field === "release_date" &&
        !isReleaseDateTimeValid(
          newForm.reporting_date,
          newForm.reporting_time,
          normalizedValue,
          newForm.release_time,
        )
      ) {
        SnackbarAlert(
          "Release date/time cannot be earlier than reporting date/time",
          "error",
        );
        return prev;
      }

      if (
        field === "reporting_date" &&
        !isReleaseDateTimeValid(
          normalizedValue,
          newForm.reporting_time,
          newForm.release_date,
          newForm.release_time,
        )
      ) {
        SnackbarAlert(
          "Reporting date/time cannot be later than release date/time",
          "error",
        );
        return prev;
      }

      return newForm;
    });
  };

  const handleTimeChange = (field, value) => {
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };

      if (
        field === "release_time" &&
        !isReleaseDateTimeValid(
          newForm.reporting_date,
          newForm.reporting_time,
          newForm.release_date,
          value,
        )
      ) {
        SnackbarAlert(
          "Release time cannot be earlier than reporting time on the same day",
          "error",
        );
        return prev;
      }

      if (
        field === "reporting_time" &&
        !isReleaseDateTimeValid(
          newForm.reporting_date,
          value,
          newForm.release_date,
          newForm.release_time,
        )
      ) {
        SnackbarAlert(
          "Reporting time cannot be later than release time on the same day",
          "error",
        );
        return prev;
      }

      return newForm;
    });
  };

  const { reporting_date, reporting_time, release_date, release_time } = form;

  const duration = calculateTotalDuration(
    reporting_date,
    reporting_time,
    release_date,
    release_time,
  );

  function formatDateDMY(dateStr) {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = String(dateStr).slice(0, 10).split("-");
    return `${dd}/${mm}/${yyyy}`;
  }

  const showItineraries = ["Outstation", "Package", "OSDayRate"].includes(
    form.duty_type,
  );

  /* ------------------------------ UI ------------------------------- */
  return (
    <Box
      onMouseDownCapture={guardMouseDown}
      onFocusCapture={guardFocus}
      sx={{
        height: "calc(100vh - 80px)", // adjust 80px if your header height is different
        overflowY: "auto",
        overflowX: "hidden",
        backgroundColor: C.page,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            p: { xs: 1, md: 1.5 },
            display: "grid",
            gap: 1,
          }}
        >
          {/* ---------- Page header ---------- */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: C.text }}>
                {editData ? "Edit booking" : "New booking"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25 }}>
                {editData
                  ? "Update the booking details and save your changes."
                  : "Enter the customer, trip and vehicle details to create a booking."}
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: "999px",
                fontSize: 12,
                fontWeight: 600,
                color: C.primary,
                backgroundColor: C.primarySoft,
                border: `1px solid #bfdbfe`,
              }}
            >
              {form.booking_status || "Pending"}
            </Box>
          </Box>

          {/* ---------- Booking details ---------- */}
          <Card title="Booking details">
            <Grid cols={6}>
              <Field
                label="Booking number"
                name="booking_no"
                value={form.booking_no}
                onChange={handleFormChange}
              />
              <BookingDateTimeField savedValue={editData?.booking_date_time} />
              <Field
                label="Reference"
                name="reference"
                value={form.reference}
                onChange={handleFormChange}
              />
              <Field
                label="Booking from"
                name="booking_from"
                value={form.booking_from}
                onChange={handleFormChange}
                options={BOOKING_FROM_OPTIONS}
              />
              <Field
                label="Booking type"
                name="accounting_type"
                value={form.accounting_type || "Confirmed"}
                onChange={handleFormChange}
                options={ACCOUNTING_TYPE_OPTIONS}
              />
              <Field
                label="Location"
                name="location_id"
                value={form.location_id || ""}
                onChange={handleFormChange}
                options={[
                  { value: "", label: "Select location" },
                  ...locations.map((loc) => ({
                    value: loc.categoryid,
                    label: loc.categoryname,
                  })),
                ]}
              />
            </Grid>
          </Card>

          {/* ---------- Customer & duty ---------- */}
          <Card title="Customer & duty">
            <Grid cols={4}>
              <Slot sx={span(2)}>
                <FormControl fullWidth size="small">
                  <CustomerDropdown
                    customers={customers}
                    selectedCustomerId={form.customer_id}
                    onChange={(customerId) =>
                      handleFormChange({
                        target: { name: "customer_id", value: customerId },
                      })
                    }
                  />
                </FormControl>
              </Slot>
              <Slot>
                <CategoryDropdown
                  clientId={clientId}
                  token={token}
                  categoryType="VehicleGroup"
                  name="vehicle_group_id"
                  label="Vehicle group"
                  value={
                    form.vehicle_group_id !== null &&
                    form.vehicle_group_id !== ""
                      ? Number(form.vehicle_group_id)
                      : null
                  }
                  onChange={handleVehicleGroupChange}
                />
              </Slot>
              <Field
                label="Duty type"
                name="duty_type"
                value={form.duty_type}
                onChange={handleFormChange}
                options={DUTY_TYPE_OPTIONS}
              />
            </Grid>

            <SubLabel>Order by (booking requester)</SubLabel>
            <Grid cols={4}>
              <Field
                label="Title"
                name="order_by_title"
                value={form.order_by_title}
                onChange={handleFormChange}
                options={TITLE_OPTIONS}
              />
              <Slot>
                <ContactAutocompleteWithAddNew
                  form={form}
                  label="Order by"
                  fieldPrefix="order_by"
                  contactType="Orderby"
                  contacts={orderByContacts}
                  formValue={form.order_by_name}
                  onFormChange={handleFormChange}
                  onValueChange={onOrderByNameChange}
                  clientId={clientId}
                  customerId={selectedCustomerId}
                  onContactAdded={handleNewOrderByContactAdded}
                />
              </Slot>
              <Field
                label="Mobile"
                value={orderByMobile}
                InputProps={{ readOnly: true }}
              />
              <Field
                label="Email"
                value={orderByEmail}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Card>

          {/* ---------- Schedule ---------- */}
          <Card title="Schedule">
            <Grid cols={4}>
              <Box ref={reportingDateWrapRef} onBlur={handleReportingDateBlur}>
                <Slot>
                  <MyDatePicker
                    label="Reporting date"
                    value={form.reporting_date}
                    onChange={(val) => handleDateChange("reporting_date", val)}
                    containerStyle={DATE_CONTAINER}
                  />
                  {reportingDateError && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "error.main",
                        mt: 0.5,
                        ml: 0.5,
                      }}
                    >
                      {reportingDateError}
                    </Typography>
                  )}
                </Slot>
              </Box>
              <Field
                label="Reporting time"
                type="time"
                name="reporting_time"
                value={form.reporting_time}
                onChange={(e) =>
                  handleTimeChange("reporting_time", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <Slot>
                <MyDatePicker
                  label="Release date"
                  value={form.release_date}
                  onChange={(val) => handleDateChange("release_date", val)}
                  containerStyle={DATE_CONTAINER}
                />
              </Slot>
              <Field
                label="Release time"
                type="time"
                name="release_time"
                value={form.release_time}
                onChange={(e) =>
                  handleTimeChange("release_time", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Box
              sx={{
                mt: 2,
                px: 1.75,
                py: 1,
                borderRadius: "8px",
                backgroundColor: duration ? C.primarySoft : C.surface,
                border: `1px solid ${duration ? "#bfdbfe" : C.border}`,
                fontSize: 13,
                color: duration ? C.text : C.muted,
              }}
            >
              {duration ? (
                <>
                  <strong>Total days:</strong> {duration.totalDays}
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <strong>Total hours:</strong> {duration.hours} hour
                  {duration.hours !== 1 ? "s" : ""} {duration.minutes} minute
                  {duration.minutes !== 1 ? "s" : ""}
                </>
              ) : (
                "Select valid reporting and release date/time to see the duration."
              )}
            </Box>
          </Card>

          {/* ---------- Passengers ---------- */}
          <Card
            title="Passenger details"
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                {passengerEditIndex !== -1 && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={cancelPassengerEdit}
                    sx={ghostBtn}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={addOrUpdatePassenger}
                  sx={primaryBtn}
                >
                  {passengerEditIndex === -1
                    ? "Add passenger"
                    : "Save passenger"}
                </Button>
              </Box>
            }
          >
            <Grid cols={4}>
              <Field
                label="Title"
                name="used_by_title"
                value={passengerDraft.used_by_title}
                onChange={handlePassengerDraftChange}
                options={TITLE_OPTIONS}
              />
              <Slot sx={span(2)}>
                <ContactAutocompleteWithAddNew
                  form={passengerDraft}
                  label="Passenger Name"
                  fieldPrefix="used_by"
                  contactType="Usedby"
                  contacts={usedByContacts}
                  formValue={passengerDraft.used_by_name}
                  onFormChange={handlePassengerFormChange}
                  onValueChange={onUsedByNameChange}
                  clientId={clientId}
                  customerId={selectedCustomerId}
                  onContactAdded={handleNewUsedByContactAdded}
                />
              </Slot>
              <Field
                label="Mobile"
                name="used_by_mobile"
                value={passengerDraft.used_by_mobile}
                onChange={handlePassengerDraftChange}
              />
              <Field
                label="Email"
                name="used_by_email"
                value={passengerDraft.used_by_email}
                onChange={handlePassengerDraftChange}
                sx={span(2)}
              />
              <Field
                label="Department"
                name="used_by_department"
                value={passengerDraft.used_by_department}
                onChange={handlePassengerDraftChange}
                sx={span(2)}
              />
              <Field
                label="Report address"
                name="report_address"
                value={passengerDraft.report_address}
                onChange={handlePassengerDraftChange}
                sx={span(2)}
              />
              <Field
                label="Drop address"
                name="drop_address"
                value={passengerDraft.drop_address}
                onChange={handlePassengerDraftChange}
                sx={span(2)}
              />
            </Grid>

            <Box
              sx={{
                mt: 2.5,
                overflowX: "auto",
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
              }}
            >
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Passenger
                    </TableCell>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Mobile
                    </TableCell>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Department
                    </TableCell>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Report location
                    </TableCell>
                    <TableCell sx={{ ...thSx, borderTop: "none" }}>
                      Drop location
                    </TableCell>
                    <TableCell
                      sx={{ ...thSx, borderTop: "none", width: 150 }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {passengers.length > 0 ? (
                    passengers.map((p, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          backgroundColor:
                            passengerEditIndex === i
                              ? C.primarySoft
                              : "transparent",
                          "&:hover": {
                            backgroundColor:
                              passengerEditIndex === i
                                ? C.primarySoft
                                : C.surface,
                          },
                          "&:last-of-type td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell sx={{ ...tdSx, fontWeight: 500 }}>
                          {p.used_by_title
                            ? `${p.used_by_title} ${p.used_by_name}`
                            : p.used_by_name}
                        </TableCell>
                        <TableCell sx={tdSx}>{p.used_by_mobile}</TableCell>
                        <TableCell sx={tdSx}>{p.used_by_email}</TableCell>
                        <TableCell sx={tdSx}>{p.used_by_department}</TableCell>
                        <TableCell sx={wrapTd}>
                          {p.report_address || "-"}
                        </TableCell>
                        <TableCell sx={wrapTd}>
                          {p.drop_address || "-"}
                        </TableCell>
                        <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                          <IconBtn
                            color="primary"
                            onClick={() => editPassenger(i)}
                            title="Edit passenger"
                          >
                            <EditIcon />
                          </IconBtn>
                          <IconBtn
                            color="error"
                            onClick={() => deletePassenger(i)}
                            title="Delete passenger"
                          >
                            <DeleteIcon />
                          </IconBtn>
                          <IconBtn
                            href={`https://wa.me/${p.used_by_mobile?.replace(
                              /\D/g,
                              "",
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Send WhatsApp message"
                            sx={{ color: "#16a34a" }}
                          >
                            <WhatsAppIcon />
                          </IconBtn>
                          <IconBtn
                            href={`mailto:${p.used_by_email}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Send email"
                            sx={{ color: C.muted }}
                          >
                            <EmailIcon />
                          </IconBtn>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyRow cols={7}>No passengers added yet.</EmptyRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Card>

          {/* ---------- Vehicle & vendor ---------- */}
          <Card title="Vehicle & vendor">
            <Grid cols={4}>
              <Slot>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={form.is_vendor_booking}
                >
                  <VehicleDropdown
                    vehicleId={form.vehicle_id}
                    clientId={clientId}
                    onVehicleChange={handleVehicleChange}
                  />
                </FormControl>
              </Slot>
              <Slot>
                <DriverDropdown
                  driverId={form.driver_id}
                  clientId={clientId}
                  onDriverChange={({ driver_id, driver_name, driver_mobile }) =>
                    setForm((f) => ({
                      ...f,
                      driver_id,
                      driver_name,
                      driver_mobile,
                    }))
                  }
                />
              </Slot>
              <Field
                label="Driver mobile"
                name="driver_mobile"
                value={form.driver_mobile || ""}
                onChange={handleFormChange}
              />
              <Box
                sx={{
                  height: FIELD_H,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  label="Vendor vehicle"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 13 },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.is_vendor_booking}
                                           onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          is_vendor_booking: e.target.checked,
                          ...(e.target.checked
                            ? {}
                            : { vendor_id: null, vendor_vehicle_number: "" }),
                        }))
                      }
                    />
                  }
                />
              </Box>
            </Grid>

            {/* Vendor fields, shown only for vendor bookings */}
            {form.is_vendor_booking && (
              <>
                <SubLabel>Vendor vehicle details</SubLabel>
                <Grid cols={4}>
                  <Slot>
                    <VendorDropdown
                      vendorId={form.vendor_id}
                      clientId={clientId}
                      onVendorChange={handleVendorChange}
                    />
                  </Slot>
                  <Field
                    label="Vendor vehicle number"
                    name="vendor_vehicle_number"
                    value={form.vendor_vehicle_number}
                    onChange={handleFormChange}
                  />
                  <Field
                    label="Driver name"
                    name="driver_name"
                    value={form.driver_name}
                    onChange={handleFormChange}
                  />
                </Grid>
              </>
            )}
          </Card>

          {/* ---------- Trip itineraries ---------- */}
          {showItineraries && (
            <Card
              title="Trip itineraries"
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {itineraryEditIndex !== -1 && (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={cancelItineraryEdit}
                      sx={ghostBtn}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={addOrUpdateItinerary}
                    sx={primaryBtn}
                  >
                    {itineraryEditIndex === -1
                      ? "Add itinerary"
                      : "Save itinerary"}
                  </Button>
                </Box>
              }
            >
              <Grid cols={4}>
                <Slot>
                  <MyDatePicker
                    label="From date"
                    value={itineraryDraft.from_date}
                    onChange={(date) =>
                      handleItineraryDraftChange("from_date", date)
                    }
                    containerStyle={DATE_CONTAINER}
                  />
                </Slot>
                <Slot>
                  <MyDatePicker
                    label="To date"
                    value={itineraryDraft.to_date}
                    onChange={(date) =>
                      handleItineraryDraftChange("to_date", date)
                    }
                    containerStyle={DATE_CONTAINER}
                  />
                </Slot>
                <Field
                  label="Days"
                  name="days"
                  type="number"
                  value={itineraryDraft.days}
                  onChange={handleItineraryFieldChange}
                />
                <Field
                  label="Travel description"
                  name="travel_description"
                  value={itineraryDraft.travel_description}
                  onChange={handleItineraryFieldChange}
                />
                <Field
                  label="Hotel name"
                  name="hotel_name"
                  value={itineraryDraft.hotel_name}
                  onChange={handleItineraryFieldChange}
                />
                <Field
                  label="Hotel address"
                  name="hotel_address"
                  value={itineraryDraft.hotel_address}
                  onChange={handleItineraryFieldChange}
                  sx={span(2)}
                />
                <Field
                  label="Hotel phone"
                  name="hotel_phone"
                  value={itineraryDraft.hotel_phone}
                  onChange={handleItineraryFieldChange}
                />
              </Grid>

              <Box
                sx={{
                  mt: 2.5,
                  overflowX: "auto",
                  border: `1px solid ${C.border}`,
                  borderRadius: "8px",
                }}
              >
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        From date
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        To date
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        Days
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        Hotel
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        Hotel address
                      </TableCell>
                      <TableCell sx={{ ...thSx, borderTop: "none" }}>
                        Hotel phone
                      </TableCell>
                      <TableCell
                        sx={{ ...thSx, borderTop: "none", width: 90 }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itineraries.length > 0 ? (
                      itineraries.map((it, i) => (
                        <TableRow
                          key={i}
                          sx={{
                            backgroundColor:
                              itineraryEditIndex === i
                                ? C.primarySoft
                                : "transparent",
                            "&:hover": {
                              backgroundColor:
                                itineraryEditIndex === i
                                  ? C.primarySoft
                                  : C.surface,
                            },
                            "&:last-of-type td": { borderBottom: "none" },
                          }}
                        >
                          <TableCell sx={tdSx}>
                            {formatDateDMY(it.from_date)}
                          </TableCell>
                          <TableCell sx={tdSx}>
                            {formatDateDMY(it.to_date)}
                          </TableCell>
                          <TableCell sx={tdSx}>{it.days}</TableCell>
                          <TableCell sx={wrapTd}>
                            {it.travel_description}
                          </TableCell>
                          <TableCell sx={tdSx}>{it.hotel_name}</TableCell>
                          <TableCell sx={wrapTd}>{it.hotel_address}</TableCell>
                          <TableCell sx={tdSx}>{it.hotel_phone}</TableCell>
                          <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                            <IconBtn
                              color="primary"
                              onClick={() => editItinerary(i)}
                              title="Edit itinerary"
                            >
                              <EditIcon />
                            </IconBtn>
                            <IconBtn
                              color="error"
                              onClick={() => deleteItinerary(i)}
                              title="Delete itinerary"
                            >
                              <DeleteIcon />
                            </IconBtn>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <EmptyRow cols={8}>No itineraries added yet.</EmptyRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          )}

          {/* ---------- Advance & payment ---------- */}
          <Card title="Advance & payment">
            <Grid cols={3}>
              <Field
                label="Advance amount"
                name="advance_amount"
                type="number"
                value={form.advance_amount}
                onChange={handleFormChange}
              />
              <Field
                label="Paid through"
                name="paid_through"
                value={form.paid_through}
                onChange={handleFormChange}
              />
              <Field
                label="Payment mode"
                name="payment_mode"
                value={form.payment_mode}
                onChange={handleFormChange}
                options={PAYMENT_MODE_OPTIONS}
              />
            </Grid>
          </Card>

          {/* ---------- Comments & remarks ---------- */}
          <Card title="Customer comments & remarks">
            <Grid cols={2} sx={{ alignItems: "start" }}>
              <Field
                label="Customer comments"
                name="customer_comments"
                multiline
                rows={3}
                value={form.customer_comments}
                onChange={handleFormChange}
                sx={{
                  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                    transform: "translate(13px, 11px) scale(1)",
                  },
                }}
              />
              <Field
                label="Remarks"
                name="remarks"
                multiline
                rows={3}
                value={form.remarks}
                onChange={handleFormChange}
                sx={{
                  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                    transform: "translate(13px, 11px) scale(1)",
                  },
                }}
              />
            </Grid>
          </Card>
        </Box>
      </Box>

      {/* ---------- Sticky action bar ---------- */}
      <Box
        data-date-guard-skip
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 1.5,
          bgcolor: "#fff",
          borderTop: `1px solid ${C.border}`,
          boxShadow: "0 -2px 8px rgba(15,23,42,0.05)",
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={isSaving}
          sx={ghostBtn}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || reportingDateInvalid}
          sx={primaryBtn}
        >
          {isSaving ? "Saving…" : editData ? "Update booking" : "Save booking"}
        </Button>
      </Box>
    </Box>
  );
}
